import { useId } from 'react';

import { cn } from '@/lib/cn';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  options: RadioOption[];
  value: string;
  onChange: (value: string) => void;
  name?: string;
  legend?: string;
  /** Lays the options out in a row instead of a column. */
  inline?: boolean;
  className?: string;
}

/**
 * A custom radio group. The native inputs stay in the DOM - hidden but
 * focusable - so arrow-key navigation within the group works without being
 * reimplemented.
 */
export function RadioGroup({
  options,
  value,
  onChange,
  name,
  legend,
  inline = false,
  className,
}: RadioGroupProps) {
  const generatedName = useId();
  const groupName = name ?? generatedName;

  return (
    <fieldset className={className}>
      {legend && <legend className="mb-1.5 text-xs font-medium text-content">{legend}</legend>}

      <div className={cn('flex gap-3', inline ? 'flex-row flex-wrap' : 'flex-col')}>
        {options.map((option) => {
          const checked = option.value === value;

          return (
            <label
              key={option.value}
              className={cn(
                'flex items-start gap-2',
                option.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
              )}
            >
              <span className="relative mt-0.5 inline-flex">
                <input
                  type="radio"
                  name={groupName}
                  value={option.value}
                  checked={checked}
                  disabled={option.disabled}
                  onChange={() => {
                    onChange(option.value);
                  }}
                  className="peer absolute h-3.5 w-3.5 cursor-pointer opacity-0"
                />
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex h-3.5 w-3.5 items-center justify-center rounded-full border transition-colors',
                    'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-1 peer-focus-visible:outline-ring',
                    checked ? 'border-primary' : 'border-border-strong',
                  )}
                >
                  {checked && <span className="h-1.5 w-1.5 rounded-full bg-primary" />}
                </span>
              </span>

              <span className="min-w-0">
                <span className="block text-sm text-content">{option.label}</span>
                {option.description && (
                  <span className="block text-xs text-content-muted">{option.description}</span>
                )}
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}
